import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    IconButton,
    Link,
    Typography,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TextField from '../components/SecretTextField';
import { LoadingSpinner } from '../components/LoadingSpinner';
import WarningDialog from '../components/WarningDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import {
    changePassword,
    changeUsername,
    confirmAccountDeletion,
    requestAccountDeletion,
    requestPasswordSetup,
} from '../services/AuthService';

import { getAccountErrorMessage } from '../services/ApiError';
import PlayerAvatar from '../components/PlayerAvatar';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { prepareProfilePicture, uploadProfilePicture, deleteProfilePicture } from '../services/ProfilePictures';

const AccountSettings = ({ embedded = false, section = 'all', onDeleted }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, logout, refreshUser } = useAuth();
    const [setupCode, setSetupCode] = useState('');

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [deleteCode, setDeleteCode] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteStep, setDeleteStep] = useState('none'); // 'none' | 'code'
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmPictureDelete, setConfirmPictureDelete] = useState(false);
    const [newUsername, setNewUsername] = useState('');
    useEffect(() => setNewUsername(''), [user?.username]);
    const titleKey = section === 'profile' ? 'auth.profile' : section === 'username' ? 'auth.changeUsername'
        : section === 'delete' ? 'auth.deleteAccount'
        : section === 'password' ? (user?.hasPassword === false ? 'auth.setPassword' : 'auth.changePassword') : 'auth.accountSettings';

    const handleChangePassword = async (event) => {
        event.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError(t('auth.errors.passwordMismatch'));
            return;
        }
        // Google-only accounts use the emailed setup code, not a current password.
        const effectiveCurrentPassword = user?.hasPassword === false ? '' : currentPassword;
        try {
            setLoading(true);
            await changePassword(effectiveCurrentPassword, newPassword, confirmPassword, setupCode);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setSetupCode('');
            setInfo(user?.hasPassword === false ? t('auth.passwordSet') : t('auth.passwordChanged'));
            await refreshUser();
        } catch (err) {
            setError(getAccountErrorMessage(err, t, 'auth.errors.changePasswordFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSetupCode = async () => {
        setError('');
        try {
            setLoading(true);
            await requestPasswordSetup();
            setInfo(t('auth.codeSent'));
        } catch (err) {
            setError(getAccountErrorMessage(err, t, 'auth.errors.requestFailed'));
        } finally { setLoading(false); }
    };

    const handlePictureUpload = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || loading) return;
        setError(''); setInfo(''); setLoading(true);
        try {
            await uploadProfilePicture(await prepareProfilePicture(file));
            await refreshUser();
            window.dispatchEvent(new CustomEvent('pwf-profile-picture-changed', { detail: { playerId: user?.playerId } }));
            setInfo(t('auth.pictureUploaded'));
        } catch { setError(t('auth.pictureUploadFailed')); }
        finally { setLoading(false); }
    };

    const handleChangeUsername = async event => {
        event.preventDefault(); setError(''); setInfo('');
        if (!/^[A-Za-z0-9_-]{3,30}$/.test(newUsername.trim())) {
            setError(t('auth.errors.invalidUsername')); return;
        }
        try {
            setLoading(true);
            await changeUsername(newUsername.trim());
            setNewUsername('');
            await refreshUser();
            setInfo(t('auth.usernameChanged'));
        } catch (err) { setError(getAccountErrorMessage(err, t, 'auth.errors.changeUsernameFailed')); }
        finally { setLoading(false); }
    };

    const handleRequestDeletion = async () => {
        setError('');
        try {
            setLoading(true);
            await requestAccountDeletion();
            setDeleteStep('code');
            setInfo(t('auth.deletionCodeSent'));
        } catch (err) {
            setError(getAccountErrorMessage(err, t, 'auth.errors.deleteFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePicture = async () => {
        setConfirmPictureDelete(false); setLoading(true); setError(''); setInfo('');
        try {
            await deleteProfilePicture(); await refreshUser();
            window.dispatchEvent(new CustomEvent('pwf-profile-picture-changed', { detail: { playerId: user?.playerId } }));
            setInfo(t('auth.pictureDeleted'));
        } catch { setError(t('auth.pictureUploadFailed')); }
        finally { setLoading(false); }
    };

    const handleConfirmDeletion = async () => {
        setError('');
        if (!/^\d{6}$/.test(deleteCode.trim())) {
            setError(t('auth.errors.invalidCode'));
            return;
        }
        try {
            setLoading(true);
            await confirmAccountDeletion(deleteCode.trim());
            if (onDeleted) await onDeleted();
            await logout();
            navigate('/login', { replace: true });
        } catch (err) {
            setError(getAccountErrorMessage(err, t, 'auth.errors.deleteFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleDeletion = async response => {
        try {
            setLoading(true); setError('');
            await confirmAccountDeletion('', response.credential);
            if (onDeleted) await onDeleted();
            await logout(); navigate('/login', { replace: true });
        } catch (err) { setError(getAccountErrorMessage(err, t, 'auth.errors.deleteFailed')); }
        finally { setLoading(false); }
    };

    return (
        <Box className={embedded ? 'account-dialog-content' : 'app-page settings-page'} aria-busy={loading} sx={{
            minHeight: embedded ? 0 : '100vh',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: 'background.default',
            padding: 3,
        }}>
            <LoadingSpinner show={loading} />
            <Box className="settings-content" sx={{ width: { xs: '100%', sm: '30rem' }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {!embedded && <Typography className="app-page-title" variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold', textAlign: 'center' }}>
                    {t(titleKey)}
                </Typography>}
                {info && (
                    <Typography role="status" variant="body2" sx={{ color: 'success.main', textAlign: 'center' }}>{info}</Typography>
                )}

                {(section === 'all' || section === 'profile') && <Card className="app-surface settings-card profile-card" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ color: 'primary.main' }}>{t('auth.profile')}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
                          <Box sx={{ position: 'relative' }}>
                            <PlayerAvatar name={user?.username} pictureDataUrl={user?.profilePictureDataUrl} sx={{ width: 72, height: 72, fontSize: '2rem' }} />
                            <IconButton component="label" color="primary" disabled={loading} aria-label={t('auth.uploadPicture')} title={t('auth.uploadPicture')}
                                size="small" sx={{ position: 'absolute', width: 26, height: 26, bottom: -6, right: -12, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                <PhotoCameraIcon sx={{ fontSize: 16 }} />
                                <input aria-label={t('auth.uploadPicture')} disabled={loading} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handlePictureUpload} />
                            </IconButton>
                            {user?.profilePictureDataUrl && <IconButton disabled={loading} color="error" aria-label={t('auth.deletePicture')}
                                title={t('auth.deletePicture')} onClick={() => setConfirmPictureDelete(true)}
                                size="small" sx={{ position: 'absolute', width: 26, height: 26, bottom: -6, left: -12, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>}
                          </Box>
                        </Box>
                        <Typography variant="body2">{t('auth.username')}: {user?.username}</Typography>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', userSelect: 'none' }}>
                            {t('auth.email')}: {user?.email}
                        </Typography>
                        {user?.isAdmin && <Typography variant="body2">{t('auth.role')}: {t('auth.adminRole')}</Typography>}
                        <Typography variant="body2">{t('auth.authenticationProvider')}: {[user?.hasPassword && t('auth.emailProvider'), user?.hasGoogleLogin && t('auth.googleProvider')].filter(Boolean).join(', ')}</Typography>
                        {!embedded && <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 1 }}>
                            <Link component={RouterLink} to="/" sx={{ color: 'primary.main' }}>{t('auth.backToLobby')}</Link>
                        </Box>}
                    </CardContent>
                </Card>}

                {(section === 'all' || section === 'username') &&
                    <Card className="app-surface settings-card" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'primary.main' }}>{t('auth.changeUsername')}</Typography>
                            <Box component="form" onSubmit={handleChangeUsername}>
                                <TextField label={t('auth.currentUsername')} fullWidth margin="normal" value={user?.username || ''}
                                    InputProps={{ readOnly: true }} />
                                <TextField label={t('auth.newUsername')} fullWidth margin="normal" required value={newUsername}
                                    autoComplete="username" inputProps={{ minLength: 3, maxLength: 30, pattern: '[A-Za-z0-9_-]{3,30}' }}
                                    onChange={event => setNewUsername(event.target.value)} />
                                <Button type="submit" fullWidth disabled={loading || !newUsername.trim() || newUsername.trim() === user?.username} variant="contained">
                                    {t('auth.changeUsername')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                }

                {/* Change / set password. Google-only accounts set a password without a current one. */}
                {(section === 'all' || section === 'password') &&
                    <Card className="app-surface settings-card" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: 'primary.main' }}>
                                {user?.hasPassword === false ? t('auth.setPassword') : t('auth.changePassword')}
                            </Typography>
                            <form onSubmit={handleChangePassword}>
                                {user?.hasPassword !== false && (
                                    <TextField
                                        label={t('auth.currentPassword')}
                                        type="password"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        required
                                        autoComplete="current-password"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                    />
                                )}
                                {user?.hasPassword === false && (
                                    <><Typography variant="body2" sx={{ color: 'success.main' }}>
                                        {t('auth.setPasswordHint')}
                                    </Typography>
                                    <Button disabled={loading} onClick={handleSetupCode}>{t('auth.sendVerificationCode')}</Button>
                                    <TextField type="password" fullWidth margin="normal" required label={t('auth.verificationCode')}
                                        value={setupCode} onChange={e => setSetupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}' }} /></>
                                )}
                                <TextField
                                    label={t('auth.newPassword')}
                                    type="password"
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    required
                                    inputProps={{ minLength: 8, maxLength: 128 }}
                                    autoComplete="new-password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                                <TextField
                                    label={t('auth.confirmPassword')}
                                    type="password"
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    required
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <Button disabled={loading} type="submit" className="app-btn" variant="contained" fullWidth>
                                    {user?.hasPassword === false ? t('auth.setPassword') : t('auth.changePassword')}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                }

                {/* Delete account */}
                {(section === 'all' || section === 'delete') && <Card className="app-surface settings-card danger-card" sx={{ backgroundColor: 'background.paper', color: 'text.primary' }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ color: 'error.main' }}>{t('auth.deleteAccount')}</Typography>
                        <Divider sx={{ marginY: 1 }} />
                        {deleteStep === 'none' ? (
                            <>
                                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                    {t('auth.deleteAccountWarning')}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    disabled={loading}
                                onClick={() => setShowDeleteConfirm(true)}
                                >
                                    {t('auth.deleteAccount')}
                                </Button>
                            </>
                        ) : user?.hasPassword === false && user?.hasGoogleLogin ? (
                            <><Typography variant="body2" sx={{ mb: 1 }}>{t('auth.googleDeleteReauth')}</Typography>
                                <GoogleSignInButton onCredential={handleGoogleDeletion} /></>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                                    {t('auth.enterDeletionCode')}
                                </Typography>
                                <TextField
                                    label={t('auth.verificationCode')}
                                    type="password"
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                    value={deleteCode}
                                    onKeyDown={event => { if (event.key === 'Enter' && !event.nativeEvent.isComposing && !loading) { event.preventDefault(); handleConfirmDeletion(); } }}
                                    onChange={(e) => setDeleteCode(e.target.value.replace(/\D/g, ''))}
                                />
                                <Button
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                    disabled={loading}
                                    onClick={handleConfirmDeletion}
                                >
                                    {t('auth.confirmDeletion')}
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>}
            </Box>

            <ConfirmDialog
                fullWidth={true}
                open={showDeleteConfirm}
                title={t('auth.deleteAccount')}
                confirmText={t('auth.deleteAccountConfirmText')}
                onCancel={() => setShowDeleteConfirm(false)}
                onHide={() => setShowDeleteConfirm(false)}
                onConfirm={() => {
                    setShowDeleteConfirm(false);
                    handleRequestDeletion();
                }}
            />
            <ConfirmDialog open={confirmPictureDelete} title={t('auth.deletePicture')} confirmText={t('auth.deletePictureConfirm')}
                onConfirm={handleDeletePicture} onCancel={() => setConfirmPictureDelete(false)} onHide={() => setConfirmPictureDelete(false)} fullWidth />
            <WarningDialog
                fullWidth={true}
                open={!!error}
                onClose={() => setError('')}
                title={t(titleKey)}
                warningText={error}
            />
        </Box>
    );
};

export default AccountSettings;
